/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  PAYMENT_MODES,
  RecordPaymentSchema,
  type RecordPaymentInput,
} from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type Invoice } from '../../lib/api/finance.js';
import { useRecordPayment } from '../../lib/queries/finance.js';
import { formatInr, rupeesToPaise } from '../../lib/utils/money.js';

interface PaymentFormProps {
  readonly invoice: Invoice;
  readonly onDone: () => void;
}

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** "Record Payment" form (Figma Finance). Validated by shared `RecordPaymentSchema`. */
export function PaymentForm({ invoice, onDone }: PaymentFormProps): JSX.Element {
  const { t } = useTranslation();
  const record = useRecordPayment();
  const balancePaise = invoice.amountPaise - invoice.amountPaidPaise;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RecordPaymentInput>({ resolver: zodResolver(RecordPaymentSchema) });

  const submit = useCallback(
    async (values: RecordPaymentInput): Promise<void> => {
      await record.mutateAsync({
        id: invoice.id,
        payload: {
          amountPaise: values.amountPaise,
          method: values.method,
          ...(values.reference !== undefined && values.reference !== ''
            ? { reference: values.reference }
            : {}),
          ...(values.paymentDate !== undefined && values.paymentDate !== ''
            ? { paymentDate: values.paymentDate }
            : {}),
        },
      });
      onDone();
    },
    [record, invoice.id, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {t('form.payment.balance', { amount: formatInr(balancePaise) })}
      </p>

      <FormField
        id="payment-amount"
        type="number"
        label={t('form.payment.amount')}
        placeholder={t('form.invoice.amountHint')}
        autoComplete="off"
        inputMode="numeric"
        error={errors.amountPaise?.message}
        registration={register('amountPaise', {
          setValueAs: (v: string) => rupeesToPaise(Number(v)),
        })}
      />

      <div>
        <label htmlFor="payment-method" className={LABEL_CLASS}>
          {t('form.payment.method')}
        </label>
        <select
          id="payment-method"
          className={SELECT_CLASS}
          defaultValue=""
          aria-invalid={errors.method !== undefined}
          {...register('method')}
        >
          <option value="" disabled>
            {t('form.payment.selectMethod')}
          </option>
          {PAYMENT_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`settings.billing.mode.${m}`)}
            </option>
          ))}
        </select>
        {errors.method !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.method.message}
          </p>
        )}
      </div>

      <FormField
        id="payment-reference"
        type="text"
        label={t('form.payment.reference')}
        autoComplete="off"
        error={errors.reference?.message}
        registration={register('reference')}
      />

      <FormField
        id="payment-date"
        type="date"
        label={t('form.payment.date')}
        autoComplete="off"
        error={errors.paymentDate?.message}
        registration={register('paymentDate')}
      />

      {record.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || record.isPending}>
          {t('form.payment.submit')}
        </Button>
      </div>
    </form>
  );
}
